// Walkthrough (guided tour) framework — Task #2088.
//
// A reusable, registry-driven tour system:
//  - `WALKTHROUGHS` — each module declares its tour here (steps optionally
//    target an element by data-testid for spotlight highlighting; steps with
//    no target render centred).
//  - Role/feature aware: a walkthrough whose featureKey/requiredPermission
//    the caller fails NEVER renders (mirrors help-article gating).
//  - State is per user, server-side (`/api/help/walkthroughs/*` — tenant DB,
//    so completion/skip persists across devices).
//  - Auto-start on first visit via `useAutoWalkthrough(id)` in the module
//    page; relaunchable from the help drawer / Help Centre via
//    `useWalkthroughs().start(id)` (restart clears the server state).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch, FeatureKey } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { useMyFeatures, useMe, type Permission } from "@/lib/auth";
import { recordHelpEvent } from "@/lib/help-analytics";

// ── Registry ─────────────────────────────────────────────────────────────────

export interface WalkthroughStep {
  title: string;
  body: string;
  /** data-testid of the element to spotlight; centred card when omitted/missing. */
  targetTestId?: string;
}

export interface WalkthroughDef {
  id: string;
  /** Help-drawer pageId this tour belongs to (drives "Take the tour" links). */
  pageId: string;
  title: string;
  description: string;
  featureKey?: FeatureKey;
  requiredPermission?: string;
  steps: WalkthroughStep[];
}

// hint: Logic changed on both sides. Requires understanding intent of each change.
export const WALKTHROUGHS: WalkthroughDef[] = [
  {
    id: "help-centre",
    pageId: "help",
    title: "Help Centre tour",
    description: "Learn your way around guides, articles and the AI assistant.",
    steps: [
      {
        title: "Welcome to the Help Centre",
        body: "This is your home for step-by-step guides, reference articles and a live-data AI assistant. Let's take a quick look around.",
      },
      {
        title: "How-To guides",
        body: "The How To tab holds curated step-by-step guides for everyday jobs — adding stock, creating sales, paying suppliers.",
        targetTestId: "tab-how-to",
      },
      {
        title: "Search everything",
        body: "Type a keyword to filter guides instantly. The Articles tab has its own search covering the full reference library.",
        targetTestId: "input-help-search",
      },
      {
        title: "Ask AI",
        body: "Can't find it? Ask the assistant in your own words — it looks up your live data (stock, profit, supplier balances) rather than guessing.",
        targetTestId: "tab-ask-ai",
      },
    ],
  },
  {
    id: "dynamic-pricing",
    pageId: "pricing",
    title: "Dynamic Pricing tour",
    description: "See how rules, runs and approvals fit together.",
    featureKey: FeatureKey.dynamic_pricing_enabled,
    steps: [
      {
        title: "The pricing engine at a glance",
        body: "Dynamic Pricing runs rules you write — spot markup, stock level, sales velocity, margin targets and more — against your catalogue, and every suggestion is clamped by a margin floor that always holds. This tour walks the full journey from rule to published price.",
      },
      {
        title: "Fees and channel costs",
        body: "Each marketplace takes a different bite — eBay fees, TikTok commission, postage. Cover them with marketplace-adjustment rules or a higher minimum margin per rule, so the price you list still delivers true profit after fees.",
      },
      {
        title: "How margins are calculated",
        body: "The engine measures margin as markup over landed cost — known before a sale, so it can be enforced up front. True margin (profit after fees, postage and affiliate costs) shows up in your reports; size your minimum margin to cover the gap.",
      },
      {
        title: "Minimum margin rules",
        body: "The floor is the higher of cost-plus-minimum-margin and the item's own minimum acceptable price. No rule — not even a manual override — can propose a price below it.",
        targetTestId: "input-minMarginPercent",
      },
      {
        title: "Channel-specific pricing",
        body: "Scope any rule to a channel to reflect that marketplace's economics; the channel is recorded on every proposal so approvers can see which platform a change targets.",
      },
      {
        title: "Price recommendations",
        body: "Run Now evaluates every enabled rule immediately (scheduled passes run up to every 6 hours). Each recommendation shows the rule, current vs proposed price, resulting margin and the exact reasoning behind it.",
        targetTestId: "button-run-pricing",
      },
      {
        title: "Approval before anything changes",
        body: "With auto-apply off, every recommendation queues on the Approvals tab for sign-off. Approve to accept, reject to discard — nothing touches a live price without a decision or an explicit auto-apply rule.",
      },
      {
        title: "Publishing to your marketplaces",
        body: "An approved (or auto-applied) change writes the new asking price to the item and pushes it to every connected channel — eBay, Shopify, WooCommerce, TikTok Shop. Every run and every change stays on the record in Run history, and Export CSV downloads the full change list.",
        targetTestId: "button-export-pricing",
      },
    ],
  },
  {
    id: "operations-inbox",
    pageId: "exceptions",
    title: "Operations Inbox tour",
    description: "Learn to triage exceptions: severity, assignment, actions, escalation and rules.",
    featureKey: FeatureKey.exception_management_enabled,
    requiredPermission: "exceptions.view",
    steps: [
      {
        title: "What is an exception?",
        body: "An exception is an operational failure that needs a human decision — an expiring marketplace authorisation, oversold stock, a failing accounting sync. Unlike a notification, it stays open, tracks an owner and deadlines, and escalates if ignored. This inbox is the one queue for all of them.",
      },
      {
        title: "Severity: P0 Critical → P5 Info",
        body: "Every exception sits in a severity band. These tabs filter by level and show live counts — P0 means drop everything (1-hour acknowledge SLA); P5 is for awareness. Within a band, the 0–100 urgency score ranks what to work first.",
        targetTestId: "tab-severity-P0",
      },
      {
        title: "Assigning work",
        body: "Use this filter to see your own queue ('Assigned to me') or problems nobody owns yet ('Unassigned'). Open any exception to assign it to yourself — a healthy team keeps unassigned P0–P2 at zero.",
        targetTestId: "filter-assignee",
      },
      {
        title: "Affected records",
        body: "Click a row to open its detail drawer. The Affected records section links straight to the orders, items or integrations involved, so you can jump from the symptom to the fix without hunting.",
      },
      {
        title: "Taking action",
        body: "In the drawer's Actions section: Acknowledge first (it stops the first escalation), assign it, then fix the underlying problem via the affected records. Every action is recorded on the exception.",
      },
      {
        title: "Resolve, snooze or wait",
        body: "Resolve with a note when it's fixed, Dismiss when no fix is needed, Snooze until a chosen time, or mark it Waiting (external/customer) when you're genuinely blocked — waiting states pause escalation. Many exceptions auto-resolve when their detector sees the condition clear.",
      },
      {
        title: "Escalation keeps you honest",
        body: "An exception left unacknowledged past its SLA bumps up a severity level and re-alerts admins; still unresolved past its resolve deadline, it bumps once more. Nothing here silently disappears.",
        targetTestId: "button-refresh",
      },
      {
        title: "Rules shape the flow",
        body: "Admins can add rules that re-prioritise, auto-assign, silence or auto-resolve new exceptions as they're raised — for example, routing every finance exception to your bookkeeper. Restart this tour any time from the Help drawer.",
        targetTestId: "link-exception-rules",
      },
    ],
  },
  {
    id: "ai-agents",
    pageId: "ai-agents",
    title: "AI Agents tour",
    description: "How agents investigate, prepare actions and wait for your approval.",
    featureKey: FeatureKey.ai_agents_enabled,
    requiredPermission: "ai_agents.view",
    steps: [
      {
        title: "Agents work in modes",
        body: "Every run gets a mode that caps what it may do: Ask answers a question, Investigate writes a findings report, Recommend suggests only notes and tasks, Prepare drafts real actions, and Execute-with-approval can auto-run only low-risk non-financial actions — if you've switched that on. Nothing here has unrestricted access.",
        targetTestId: "tab-catalogue",
      },
      {
        title: "Preparing actions, not taking them",
        body: "In Prepare mode an agent drafts proposals — a sync retry, a stock adjustment, a draft purchase order — with its full reasoning and evidence attached. Proposals change nothing until a human decides.",
      },
      {
        title: "The Approval Centre",
        body: "Every proposal queues here. Open one to see the agent's reason, confidence, data sources, assumptions and the exact records affected — then approve it (fully or partially), reject it, or send it back for re-analysis.",
        targetTestId: "tab-approvals",
      },
      {
        title: "Risk levels 0–5",
        body: "Each action type carries a fixed risk score: a note is 0, a stock adjustment is 3, price changes and draft POs are 4, refunds are 5. Levels 4–5 are financial and always require approval — no setting can change that.",
      },
      {
        title: "Guardrails hold, always",
        body: "Deterministic server-side limits — max PO value, max price change, minimum margin, max affected records, allowed marketplaces, max refund — are checked before any execution, even after you approve. A failed guardrail blocks the action and tells you exactly why.",
      },
      {
        title: "Scheduled agents",
        body: "Put an agent on a clock — hourly, daily or weekly (UTC) — with a standing instruction and mode. Scheduled runs follow exactly the same rules as manual ones, and cost limits pause schedules automatically.",
        targetTestId: "tab-schedules",
      },
      {
        title: "Everything on the record",
        body: "Run history keeps every run: the instruction, every data query the agent made, its findings, its proposals, tokens and cost. Every approval, rejection and execution result is attributed and timestamped.",
        targetTestId: "tab-runs",
      },
      {
        title: "Costs you control",
        body: "Daily and monthly spend caps, a per-run token budget and a concurrency limit keep AI costs predictable — runs are refused before a limit would be breached, never billed past it. Settings holds the caps alongside the guardrails.",
        targetTestId: "tab-settings",
      },
    ],
  },
];

export function getWalkthroughById(id: string): WalkthroughDef | undefined {
  return WALKTHROUGHS.find((w) => w.id === id);
}

export function getWalkthroughsForPage(pageId: string): WalkthroughDef[] {
  return WALKTHROUGHS.filter((w) => w.pageId === pageId);
}

// ── Server state ─────────────────────────────────────────────────────────────

type WalkthroughStateRow = { walkthroughId: string; status: string; updatedAt: string | null };
const STATE_KEY = ["/api/help/walkthroughs/state"];

export function useWalkthroughStates() {
  return useQuery({
    queryKey: STATE_KEY,
    queryFn: () =>
      customFetch<{ states: WalkthroughStateRow[] }>("/api/help/walkthroughs/state"),
    staleTime: 60_000,
    retry: 1,
  });
}

// ── Context / provider ───────────────────────────────────────────────────────

interface WalkthroughContextValue {
  activeId: string | null;
  /** Start (or restart) a tour. Restart clears the stored completion state. */
  start: (id: string, opts?: { restart?: boolean }) => void;
  stop: () => void;
  /** Gating check shared with the drawer/Help Centre relaunch lists. */
  canSee: (def: WalkthroughDef) => boolean;
}

const WalkthroughContext = createContext<WalkthroughContextValue>({
  activeId: null,
  start: () => undefined,
  stop: () => undefined,
  canSee: () => false,
});

export function useWalkthroughs() {
  return useContext(WalkthroughContext);
}

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const { hasFeature } = useMyFeatures();
  const { has } = useMe();
  const queryClient = useQueryClient();

  const canSee = useCallback(
    (def: WalkthroughDef) =>
      (!def.featureKey || hasFeature(def.featureKey)) &&
      (!def.requiredPermission || has(def.requiredPermission as Permission)),
    [hasFeature, has],
  );

  const persist = useCallback(
    (id: string, status: "completed" | "skipped") => {
      void customFetch(`/api/help/walkthroughs/${encodeURIComponent(id)}/state`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      })
        .then(() => queryClient.invalidateQueries({ queryKey: STATE_KEY }))
        .catch(() => undefined);
      recordHelpEvent({
        eventType: status === "completed" ? "walkthrough_completed" : "walkthrough_skipped",
        walkthroughId: id,
      });
    },
    [queryClient],
  );

  const start = useCallback(
    (id: string, opts?: { restart?: boolean }) => {
      const def = getWalkthroughById(id);
      if (!def || !canSee(def)) return;
      if (opts?.restart) {
        void customFetch(`/api/help/walkthroughs/${encodeURIComponent(id)}/state`, {
          method: "DELETE",
        })
          .then(() => queryClient.invalidateQueries({ queryKey: STATE_KEY }))
          .catch(() => undefined);
      }
      recordHelpEvent({ eventType: "walkthrough_started", walkthroughId: id });
      setStepIndex(0);
      setActiveId(id);
    },
    [canSee, queryClient],
  );

  const stop = useCallback(() => setActiveId(null), []);

  const finish = useCallback(() => {
    if (activeId) persist(activeId, "completed");
    setActiveId(null);
  }, [activeId, persist]);

  const skip = useCallback(() => {
    if (activeId) persist(activeId, "skipped");
    setActiveId(null);
  }, [activeId, persist]);

  const value = useMemo(
    () => ({ activeId, start, stop, canSee }),
    [activeId, start, stop, canSee],
  );

  const activeDef = activeId ? getWalkthroughById(activeId) : undefined;

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
      {activeDef && (
        <WalkthroughOverlay
          def={activeDef}
          stepIndex={stepIndex}
          onNext={() =>
            stepIndex >= activeDef.steps.length - 1
              ? finish()
              : setStepIndex((i) => i + 1)
          }
          onBack={() => setStepIndex((i) => Math.max(0, i - 1))}
          onSkip={skip}
        />
      )}
    </WalkthroughContext.Provider>
  );
}

/**
 * Auto-start a walkthrough on first visit to a module. No-ops when the user
 * has already completed/skipped it (server state), when gating fails, or when
 * another tour is running. Call from the module page component.
 */
export function useAutoWalkthrough(id: string) {
  const { start, activeId, canSee } = useWalkthroughs();
  const { data, isSuccess } = useWalkthroughStates();
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current || activeId) return;
    if (!isSuccess) return;
    const def = getWalkthroughById(id);
    if (!def || !canSee(def)) return;
    const seen = data?.states.some((s) => s.walkthroughId === id);
    if (seen) return;
    firedRef.current = true;
    // Small delay so the page has painted before we measure targets.
    const t = setTimeout(() => start(id), 600);
    return () => clearTimeout(t);
  }, [id, isSuccess, data, activeId, canSee, start]);
}

// ── Overlay UI ───────────────────────────────────────────────────────────────

function WalkthroughOverlay({
  def,
  stepIndex,
  onNext,
  onBack,
  onSkip,
}: {
  def: WalkthroughDef;
  stepIndex: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const step = def.steps[stepIndex];
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    let raf = 0;
    const measure = () => {
      if (!step?.targetTestId) {
        setRect(null);
        return;
      }
      const el = document.querySelector<HTMLElement>(
        `[data-testid="${step.targetTestId}"]`,
      );
      if (!el) {
        setRect(null);
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      raf = requestAnimationFrame(() => setRect(el.getBoundingClientRect()));
    };
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [step?.targetTestId, stepIndex]);

  if (!step) return null;
  const isLast = stepIndex === def.steps.length - 1;

  // Card position: below the target when highlighted, otherwise centred.
  const pad = 8;
  const cardStyle: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: Math.min(rect.bottom + 12, window.innerHeight - 220),
        left: Math.min(Math.max(rect.left, 16), Math.max(16, window.innerWidth - 400)),
        zIndex: 10002,
      }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 10002,
      };

  return createPortal(
    <div data-testid={`walkthrough-${def.id}`}>
      {/* Dimmer — spotlight cut-out via box-shadow when a target is found. */}
      {rect ? (
        <div
          className="pointer-events-none"
          style={{
            position: "fixed",
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 8,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
            border: "2px solid hsl(var(--primary))",
            zIndex: 10001,
            transition: "all 200ms ease",
          }}
        />
      ) : (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 10001,
          }}
        />
      )}

      <div
        style={cardStyle}
        className="w-[360px] max-w-[calc(100vw-32px)] rounded-xl border bg-card p-5 shadow-2xl"
        role="dialog"
        aria-label={`${def.title} — step ${stepIndex + 1} of ${def.steps.length}`}
      >
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
          {def.title} · {stepIndex + 1} / {def.steps.length}
        </div>
        <h3 className="text-sm font-semibold mb-1.5">{step.title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
            data-testid="walkthrough-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={onBack} data-testid="walkthrough-back">
                Back
              </Button>
            )}
            <Button size="sm" onClick={onNext} data-testid="walkthrough-next">
              {isLast ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
